import {
  Coins, Wrench, LayoutDashboard, Megaphone, Siren, Activity, RefreshCw, ShieldCheck, Check, X,
} from "lucide-react";
import { Seo } from "@/components/common/Seo";
import { PageHero } from "@/components/common/PageHero";
import { Reveal, RevealGroup, RevealItem } from "@/components/common/Reveal";
import { SectionLabel } from "@/components/common/SectionLabel";
import { CTAButton } from "@/components/common/CTAButton";
import { LeadForm } from "@/components/common/LeadForm";
import { SlabMatrix } from "@/components/common/SlabMatrix";
import { OccupancyEstimator } from "@/components/common/OccupancyEstimator";
import { EditorialMarquee } from "@/components/common/EditorialMarquee";
import {
  SEO, COMPARISON, SOCIETY_BENEFITS, AGREEMENT_CALLOUTS, SOCIETY_FAQ,
} from "@/data/site";

const icons = { Coins, Wrench, LayoutDashboard, Megaphone, Siren, Activity, RefreshCw, ShieldCheck };

const Comparison = () => (
  <section className="bg-paper py-24 md:py-32">
    <div className="container-px">
      <Reveal><SectionLabel>The AdX Difference</SectionLabel></Reveal>
      <Reveal delay={0.05}>
        <h2 className="mt-6 font-display font-800 text-4xl md:text-6xl tracking-tight max-w-3xl text-balance">
          What they offer vs. what AdX offers.
        </h2>
      </Reveal>
      <div className="mt-14 overflow-hidden rounded-xl border border-ink/10 bg-white">
        <div className="grid grid-cols-12 bg-ink text-white font-mono text-xs uppercase tracking-[0.18em]">
          <div className="col-span-4 p-4 md:p-5">Dimension</div>
          <div className="col-span-4 p-4 md:p-5 text-white/50">Traditional DOOH</div>
          <div className="col-span-4 p-4 md:p-5 text-teal">AdX</div>
        </div>
        {COMPARISON.map((row, i) => (
          <div key={i} className={`grid grid-cols-12 items-center border-t border-ink/10 ${i % 2 ? "bg-paper/60" : "bg-white"}`}>
            <div className="col-span-4 p-4 md:p-5 font-display font-600 text-sm md:text-base">{row.dim}</div>
            <div className="col-span-4 p-4 md:p-5 text-ink/50 text-sm flex items-start gap-2">
              <X className="h-4 w-4 text-ink/30 shrink-0 mt-0.5" /> <span>{row.old}</span>
            </div>
            <div className="col-span-4 p-4 md:p-5 text-ink text-sm flex items-start gap-2">
              <Check className="h-4 w-4 text-teal shrink-0 mt-0.5" strokeWidth={2.5} /> <span>{row.adx}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Benefits = () => (
  <section className="bg-white py-24 md:py-32 border-y border-ink/10">
    <div className="container-px">
      <Reveal><SectionLabel>What You Get</SectionLabel></Reveal>
      <Reveal delay={0.05}>
        <h2 className="mt-6 font-display font-800 text-4xl md:text-6xl tracking-tight max-w-2xl text-balance">
          Everything, covered.
        </h2>
      </Reveal>
      <RevealGroup className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-ink/10 border border-ink/10 rounded-xl overflow-hidden">
        {SOCIETY_BENEFITS.map((b) => {
          const Icon = icons[b.icon];
          return (
            <RevealItem key={b.title} className="bg-paper p-7 group transition-colors duration-300 hover:bg-white">
              <Icon className="h-7 w-7 text-teal" strokeWidth={1.6} />
              <h3 className="mt-6 font-display font-700 text-lg">{b.title}</h3>
              <p className="mt-3 text-sm text-ink/60 leading-relaxed">{b.body}</p>
            </RevealItem>
          );
        })}
      </RevealGroup>
    </div>
  </section>
);

const Agreement = () => (
  <section className="relative bg-ink text-white py-24 md:py-32 overflow-hidden">
    <div className="pointer-events-none absolute top-0 left-1/3 h-80 w-80 rounded-full bg-teal/15 blur-[130px]" />
    <div className="relative container-px">
      <Reveal><SectionLabel dark>Our Agreement Is Different</SectionLabel></Reveal>
      <Reveal delay={0.05}>
        <h2 className="mt-6 font-display font-800 text-4xl md:text-6xl tracking-tight max-w-3xl text-balance">
          A contract your committee will feel good about signing.
        </h2>
      </Reveal>
      <RevealGroup className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-4">
        {AGREEMENT_CALLOUTS.map((c, i) => (
          <RevealItem key={i} className="flex items-center gap-4 border border-white/10 rounded-lg p-5 bg-white/[0.03]">
            <span className="font-display font-800 text-teal text-lg tabular-nums">{String(i + 1).padStart(2, "0")}</span>
            <span className="font-display font-600 text-lg">{c}</span>
          </RevealItem>
        ))}
      </RevealGroup>
    </div>
  </section>
);

const Hardware = () => (
  <section className="bg-paper py-24 md:py-32">
    <div className="container-px grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
      <div>
        <Reveal><SectionLabel>The Hardware</SectionLabel></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-6 font-display font-800 text-4xl md:text-5xl tracking-tight text-balance">
            Smart LED Screens. Clean. Quiet. Non-intrusive.
          </h2>
        </Reveal>
        <RevealGroup className="mt-10 grid grid-cols-2 gap-px bg-ink/10 border border-ink/10 rounded-xl overflow-hidden">
          {[
            ["Screen", "32″ portrait LED"],
            ["Power", "~40W"],
            ["Operating hours", "06:00 – 23:30"],
            ["Offline detection", "Within 3 minutes"],
          ].map(([k, v]) => (
            <RevealItem key={k} className="bg-white p-6">
              <p className="font-mono text-xs uppercase tracking-[0.18em] text-ink/45">{k}</p>
              <p className="mt-2 font-display font-700 text-xl">{v}</p>
            </RevealItem>
          ))}
        </RevealGroup>
        <Reveal delay={0.1}>
          <p className="mt-8 text-ink/60 leading-relaxed max-w-md">
            Custom casing, concealed wiring and flush-mount options - installed to respect your lobby, not clutter it. Every device is insured and monitored remotely.
          </p>
        </Reveal>
      </div>
      <Reveal delay={0.1}>
        <div className="relative overflow-hidden rounded-xl border border-ink/10 aspect-[4/5]">
          <img src="/hardware-lobby.png" alt="Digital advertising screen between two lift gates in a residential lobby" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/40 to-transparent" />
        </div>
      </Reveal>
    </div>
  </section>
);

const Faq = () => (
  <section className="bg-white py-24 md:py-32 border-y border-ink/10">
    <div className="container-px grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-4">
        <Reveal><SectionLabel>FAQ</SectionLabel></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-6 font-display font-800 text-4xl md:text-5xl tracking-tight text-balance">
            Questions, answered plainly.
          </h2>
        </Reveal>
      </div>
      <div className="lg:col-span-8">
        <RevealGroup className="divide-y divide-ink/10 border-t border-ink/10">
          {SOCIETY_FAQ.map((f, i) => (
            <RevealItem key={i} className="py-6">
              <h3 className="font-display font-700 text-lg flex gap-3">
                <span className="text-teal font-mono text-sm mt-1">Q{i + 1}</span> {f.q}
              </h3>
              <p className="mt-3 pl-9 text-ink/60 leading-relaxed">{f.a}</p>
            </RevealItem>
          ))}
        </RevealGroup>
      </div>
    </div>
  </section>
);

const Payout = () => (
  <section id="payout" className="bg-white py-24 md:py-32 border-b border-ink/10">
    <div className="container-px">
      <Reveal><SectionLabel>Your Payout & Revenue</SectionLabel></Reveal>
      <Reveal delay={0.05}>
        <h2 className="mt-6 font-display font-800 text-4xl md:text-6xl tracking-tight max-w-3xl text-balance">
          Transparent, slab-based payouts.
        </h2>
      </Reveal>
      <Reveal delay={0.08}>
        <p className="mt-6 max-w-2xl text-ink/60 leading-relaxed">
          Payouts are structured transparently by the number of flats and occupancy, with electricity included. The figures below are indicative - final commercial terms are confirmed per community.
        </p>
      </Reveal>
      <Reveal delay={0.12}>
        <div className="mt-12"><SlabMatrix /></div>
      </Reveal>
      <Reveal delay={0.05}>
        <div className="mt-16">
          <SectionLabel>Occupancy Estimator</SectionLabel>
          <h3 className="mt-6 font-display font-700 text-2xl md:text-3xl tracking-tight">
            See exactly what your community earns.
          </h3>
          <div className="mt-8"><OccupancyEstimator /></div>
        </div>
      </Reveal>
    </div>
  </section>
);

const ProposalCta = () => (
  <section className="bg-paper py-24 md:py-32">
    <div className="container-px grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
      <div className="lg:col-span-5">
        <Reveal><SectionLabel>Get Started</SectionLabel></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-6 font-display font-800 text-4xl md:text-5xl tracking-tight text-balance">
            Get a proposal for your society.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 text-ink/60 leading-relaxed max-w-md">
            Tell us about your community and we'll put together a transparent proposal - screen placement, payout and full agreement terms with no hidden clauses.
          </p>
        </Reveal>
      </div>
      <div className="lg:col-span-7">
        <LeadForm variant="society" ctaLabel="Request a proposal" testidPrefix="society-lead"
          note="Fair terms, zero cost, full transparency. We'll respond within 2 business days." />
      </div>
    </div>
  </section>
);

export default function Societies() {
  return (
    <>
      <Seo title={SEO.societies.title} description={SEO.societies.description} />
      <PageHero
        label="For Societies"
        titleLines={["Your Lobby.", "Your Terms."]}
        accentIndex={1}
        subtitle="AdX installs premium digital screens in your elevator lobbies at zero cost - and pays a competitive monthly rent with genuinely fair contract terms."
      >
        <div className="flex flex-col sm:flex-row gap-4">
          <CTAButton href="#proposal" variant="primary" data-testid="society-hero-cta">Get a proposal</CTAButton>
          <CTAButton href="#payout" variant="ghostLight">See your payout</CTAButton>
        </div>
      </PageHero>
      <EditorialMarquee items={["Transparent payouts", "Clear terms", "Electricity included", "3-year terms", "Full transparency"]} />
      <Comparison />
      <Payout />
      <Benefits />
      <Agreement />
      <Hardware />
      <Faq />
      <div id="proposal"><ProposalCta /></div>
    </>
  );
}
